import { useEffect, useState, useCallback, useRef } from 'react';

const WS_URL = process.env.REACT_APP_WS_URL || 'wss://backupmanagerbackend.amygbserver.in';

export const useWebSocket = (onMessage, enabled = true) => {
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  
  // 1. Store the callback in a ref so 'connect' doesn't have to depend on it
  const onMessageRef = useRef(onMessage);
  
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  const connect = useCallback(() => {
    if (!enabled) return;

    // Clean up any existing connection/timeout before starting a new one
    if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
    if (wsRef.current) wsRef.current.close();

    try {
      const ws = new WebSocket(WS_URL);

      ws.onopen = () => {
        console.log('WebSocket connected');
        setConnected(true);
        setError(null);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          // 2. Use the ref here
          if (onMessageRef.current) {
            onMessageRef.current(data);
          }
        } catch (err) {
          console.error('Failed to parse WebSocket message:', err);
        }
      };

      ws.onerror = (err) => {
        // setError triggers a re-render; since connect is stable, this is now safe
        setError('WebSocket connection error');
      };

      ws.onclose = (event) => {
        console.log('WebSocket disconnected');
        setConnected(false);
        
        // 3. Only reconnect if the close wasn't intentional (wasn't closed by 'disconnect')
        if (!event.wasClean) {
            reconnectTimeoutRef.current = setTimeout(() => {
              console.log('Attempting to reconnect...');
              connect();
            }, 3000);
        }
      };

      wsRef.current = ws;
    } catch (err) {
      setError('Failed to connect');
    }
    // Removed [onMessage] - this makes 'connect' stable!
  }, [enabled]); 

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      // Use a "clean" close code so the onclose listener knows not to reconnect
      wsRef.current.close(1000, "Component Unmounted");
      wsRef.current = null;
    }
  }, []);

  const send = useCallback((data) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      disconnect();
      setConnected(false);
      setError(null);
      return undefined;
    }

    connect();
    return () => disconnect();
  }, [connect, disconnect, enabled]);

  return { connected, error, send };
};
